import { createSelector, createStructuredSelector } from 'reselect';
import moment from 'moment';

import { getUser, getAmplitudeInstance, getUserIsHost, getUserSlug, getUserUnreadMessagesCount, getUserMemberships } from '../../modules/session/getters';
import { getHostId, getHostCurrencies } from '../organization-select/getters';
// Balances
const getAccountBalancesLoading = (state) => state.dashboard.balances.loading;
const getAccountBalances = (state) => state.dashboard.balances.data;

// Quick Links
const getQuickLinks = (state) => state.dashboard.ui.quickLinks;

// Report
const getReportLoading = (state) => state.dashboard.report.loading;
const getUserHasFreeEventsOnly = (state) => state.dashboard.report.data.has_free_listings_only;

export const getReportOptions = (state) => state.dashboard.reportOpts.options;
export const getReportSelected = (state) => state.dashboard.reportOpts.reportSelected;
const getReportTimeRangeSelected = (state) => state.dashboard.reportOpts.timeRangeSelected;
const getReportCustomTimeRange = (state) => state.dashboard.reportOpts.customTimeRange;
const getReportSingleDate = (state) => state.dashboard.reportOpts.singleDate;
const getReportFocusedInput = (state) => state.dashboard.reportOpts.focusedInput;

// Custom Events
const getSelectedEventsModalIsOpen = (state) => state.dashboard.selectedEventsModal.isOpen;
export const getCustomSelectedSlugsFromModal = (state) => state.dashboard.selectedEventsModal.selected;
export const getCustomSelectedEvents = (state) => state.dashboard.selectedEventsModal.events;
export const getSelectedEventsSlugs = (state) => state.dashboard.selectedEvents.slugs;
const getSelectedEventsObject = (state) => state.dashboard.selectedEvents.events;

const getSelectedTime = (state) => state.dashboard.selectedTime.selectedTime;
const getCustomTimeRange = (state) => state.dashboard.selectedTime.customTimeRange;
const getTempTimeRange = (state) => state.dashboard.selectedTime.tempTimeRange;
const getSelectedTimeFocusedInput = (state) => state.dashboard.selectedTime.focusedInput;
const getSelectedCurrency = (state) => state.dashboard.selectedCurrency;

const getSalesChartError = (state) => state.dashboard.salesChartError;

export const PAST_7_DAYS = 'wtd';
export const PAST_30_DAYS = 'mtd';
export const YEAR_TO_DATE = 'ytd';
export const CUSTOM = 'custom';

// Selectors
export const getReportTimeRange = createSelector(
  [getReportTimeRangeSelected, getReportCustomTimeRange, getReportSingleDate],
  (timeRange, customRange, singleDate) => {
    if (timeRange === PAST_30_DAYS) {
      const monthAgo = moment().subtract(1, 'month').format('YYYY-MM-DD');
      const now = moment().format('YYYY-MM-DD');
      return `?from=${monthAgo}&to=${now}`;
    }
    else if (timeRange === 'custom') {
      const from = moment(customRange.startDate).format('YYYY-MM-DD');
      const to = moment(customRange.endDate).format('YYYY-MM-DD');
      return `?from=${from}&to=${to}`;
    }
    else if (timeRange === 'single') {
      const from = moment(singleDate).format('YYYY-MM-DD');
      const to = moment(singleDate).format('YYYY-MM-DD');
      return `?from=${from}&to=${to}`;
    }

    return '?'; // default (all transactions)
  }
);

const getCustomReportDateStr = createSelector(
  [getReportCustomTimeRange],
  (customRange) => {
    return moment(customRange.startDate).format('MMM DD, YYYY') + ' - ' +
      moment(customRange.endDate).format('MMM DD, YYYY');
  }
);

export const getSelectedReportTitle = createSelector(
  [getReportOptions, getReportSelected],
  (reportOptions, reportSelected) => {
    if (reportSelected !== 'default') {
      return reportOptions.reports.filter(report => report.value === reportSelected)[0].title || '';
    }

    return '';
  }
);

export const getSelectedReportId = createSelector(
  [getReportOptions, getReportSelected],
  (reportOptions, reportSelected) => {
    if (reportSelected !== 'default') {
      return reportOptions.reports.filter(report => report.value === reportSelected)[0].value || '';
    }

    return 'default';
  }
);

const getTickOffset = createSelector(
  [getSelectedTime],
  (selectedTime) => {
    if (selectedTime === PAST_30_DAYS || selectedTime === YEAR_TO_DATE) {
      return 7;
    }
    else if (selectedTime === PAST_7_DAYS) {
      return 1;
    }

    return 1;
  }
);

export const getDateFilter = createSelector(
  [getSelectedTime, getCustomTimeRange],
  (selectedTime, customTimeRange) => {
    const now = moment().format('YYYY-MM-DD');
    const defaultParams = {
      from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      to: now
    };

    if (selectedTime === PAST_30_DAYS) {
      return defaultParams;
    }
    else if (selectedTime === PAST_7_DAYS) {
      return {
        from: moment().subtract(1, 'week').format('YYYY-MM-DD'),
        to: now
      };
    }
    else if (selectedTime === YEAR_TO_DATE) {
      return {
        from: moment().startOf('year').format('YYYY-MM-DD'),
        to: now
      };
    }
    else if (selectedTime === CUSTOM) {
      return {
        from: moment(customTimeRange.startDate).format('YYYY-MM-DD'),
        to: customTimeRange.endDate ? moment(customTimeRange.endDate).format('YYYY-MM-DD') : now
      };
    }

    return defaultParams;
  }
);

const getCurrentOrganization = createSelector(
  [getUserMemberships, getHostId, getUser],
  (memberships, hostId, user) => {
    // viewing their own profile, no org.
    if (!user || hostId === user.id) {
      return null;
    }

    return memberships.find(membership => membership.organization.owner.id === hostId);
  }
);

export const getHasPermissionForDashboard = createSelector(
  [getCurrentOrganization],
  (currentOrganization) => currentOrganization ? currentOrganization.manage_overview : true,
);

// ['Jan 1', 'Jan 8', 'Jan 15', 'Jan 22'];
const getNetSalesTicks = createSelector(
  [getSelectedTime, getTickOffset],
  (selectedTime, tickOffset) => {
    let currentDay = moment().subtract(30, 'days');

    if (selectedTime === PAST_30_DAYS) {
      currentDay = moment().subtract(30, 'days');
    }
    else if (selectedTime === PAST_7_DAYS) {
      currentDay = moment().subtract(1, 'week');
    }
    else if (selectedTime === YEAR_TO_DATE) {
      currentDay = moment().startOf('year');
    }

    const ticks = [];

    while (currentDay.isBefore(moment())) {
      ticks.push(currentDay.format('MMM D'));
      currentDay = currentDay.add(tickOffset, 'days');
    }

    return ticks;
  }
);

export default createStructuredSelector({
  accountBalancesLoading: getAccountBalancesLoading,
  balances: getAccountBalances,
  currencies: getHostCurrencies,
  customDateStr: getCustomReportDateStr,
  customSelectedEvents: getCustomSelectedSlugsFromModal,
  dateFilter: getDateFilter,
  dashboardCustomTimeRange: getCustomTimeRange,
  netSalesTicks: getNetSalesTicks,
  reportOptions: getReportOptions,
  reportSelected: getReportSelected,
  reportTimeRangeSelected: getReportTimeRangeSelected,
  reportCustomTimeRange: getReportCustomTimeRange,
  reportFocusedInput: getReportFocusedInput,
  reportSingleDate: getReportSingleDate,
  selectedCurrency: getSelectedCurrency,
  selectedEvents: getSelectedEventsObject,
  selectedEventsSlugs: getSelectedEventsSlugs,
  selectedEventsModalIsOpen: getSelectedEventsModalIsOpen,
  selectedReportId: getSelectedReportId,
  selectedReportTitle: getSelectedReportTitle,
  selectedTime: getSelectedTime,
  selectedTimeFocusedInput: getSelectedTimeFocusedInput,
  tempTimeRange: getTempTimeRange,
  quickLinks: getQuickLinks,
  reportLoading: getReportLoading,
  user: getUser,
  hostId: getHostId,
  amplitudeInstance: getAmplitudeInstance,
  userHasFreeEventsOnly: getUserHasFreeEventsOnly,
  userIsHost: getUserIsHost,
  userSlug: getUserSlug,
  userUnreadMessagesCount: getUserUnreadMessagesCount,
  salesChartError: getSalesChartError,
  hasPermissionForDashboard: getHasPermissionForDashboard
});



// WEBPACK FOOTER //
// ./src/modules/dashboard/getters.js